import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useUrlSearchParams } from '@/hooks/useUrlSearchParams'
import { getCustomerTickets } from '@/modules/Customer/services'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getCustomerTicketsColumns } from './customer-tickets-config'

interface UseCustomerTicketsParams {
    customerId: number
    startDate: string
    endDate: string
    enabled?: boolean
}

/**
 * Hook for Customer Tickets List
 * Manages data fetching, pagination, and column configuration
 */
export const useCustomerTickets = ({
    customerId,
    startDate,
    endDate,
    enabled = true,
}: UseCustomerTicketsParams) => {
    const { t } = useTranslation('ticket')

    // URL params for pagination with prefix to avoid conflicts
    const { params, setParams } = useUrlSearchParams({
        defaults: {
            page: 1,
            page_size: 10,
        },
        prefix: 'tickets_',
    })

    const page = params.page || 1
    const pageSize = params.page_size || 10

    // Fetch tickets data
    const {
        data: ticketsData,
        isLoading,
        isFetching,
    } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.TICKETS,
            customerId,
            startDate,
            endDate,
            page,
            pageSize,
        ],
        queryFn: () =>
            getCustomerTickets(customerId, {
                limit: pageSize,
                offset: (page - 1) * pageSize,
                start_date: startDate,
                end_date: endDate,
            }),
        enabled: enabled && !!customerId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    })

    const tickets = ticketsData?.results || []
    const total = ticketsData?.pagination?.total || tickets.length

    // Calculate summary statistics
    const stats = useMemo(
        () => ({
            total,
            opening: tickets.filter((t) => t.ticket_status === 'opening')
                .length,
            inProgress: tickets.filter(
                (t) => t.ticket_status === 'work-in-progress'
            ).length,
            pending: tickets.filter((t) => t.ticket_status === 'pending')
                .length,
            resolved: tickets.filter((t) => t.ticket_status === 'resolved')
                .length,
            closed: tickets.filter((t) => t.ticket_status === 'closed').length,
        }),
        [tickets, total]
    )

    // Active tickets count (not closed/resolved)
    const activeCount = stats.opening + stats.inProgress + stats.pending

    // Memoize columns
    const columns = useMemo(() => getCustomerTicketsColumns(t), [t])

    // Pagination handlers
    const handlePageChange = (newPage: number) => {
        setParams({ page: newPage })
    }

    const handlePageSizeChange = (newSize: number) => {
        setParams({
            page: 1,
            page_size: newSize,
        })
    }

    return {
        // Data
        tickets,
        total,
        stats,
        activeCount,

        // State
        isLoading,
        isFetching,
        page,
        pageSize,

        // Config
        columns,

        // Handlers
        handlePageChange,
        handlePageSizeChange,

        // Translation
        t,
    }
}
